// ─── Domain types (matching the DB schema / API responses) ───────────────────

import type { SourceTier } from '../types/story'

/**
 * Phase 2 vendor unification (Plan T11) — read-side namespace shapes that
 * mirror `apps/api/src/projections/types.ts`. Every vendor-bearing API
 * endpoint emits `VendorRef[]`/`PersonRef[]` after Phase 2.
 *
 * - `tier`: numeric tier derived from `vendors.vendor_kind`. Currently
 *   only `hyperscaler` and `identity` map to Tier-1 (`1`); everything
 *   else resolves to `null`. UI: render a small "T1" pill when `tier === 1`.
 * - `aliasOf`: forward-compat slot for Phase 3a alias-merge results. Always
 *   `undefined` in Phase 2 — included so the contract is stable across
 *   the Phase 3a ship.
 */
export interface VendorRef {
  id: string
  name: string
  tier: number | null
  aliasOf?: string
}

/**
 * Phase 2 vendor unification (Plan T11) — Person reference. `affiliatedVendorId`
 * is omitted entirely when the persons row's `affiliated_vendor_id` is NULL
 * (matches the API projector's behaviour).
 */
export interface PersonRef {
  id: string
  displayName: string
  affiliatedVendorId?: string
}

export type Severity = 'critical' | 'high' | 'medium' | 'low'
export type Confidence = 'confirmed' | 'high' | 'medium' | 'low'
export type ArticleType = 'report' | 'incident' | 'vendor_intel' | 'industry_news' | 'security_guide'
export type Category = 'threat' | 'vulnerability' | 'cloud' | 'identity' | 'regulation' | 'risk' | 'supply_chain'
export type Section =
  | 'threat_landscape_ir'
  | 'vulnerabilities_exploitation'
  | 'cloud_saas_identity'
  | 'supply_chain_third_party'
  | 'vendor_intelligence'
  | 'industry_news'
  | 'governance_risk_compliance'
  | 'security_awareness'
export type ContentStatus = 'draft' | 'published' | 'suppressed' | 'archived'
export type ExploitationStatus = 'none' | 'poc' | 'active' | 'widespread'
export type RegulatoryType = 'new_rule' | 'guidance' | 'enforcement' | 'deadline'
export type ContentType = 'article' | 'regulatory' | 'benchmark' | 'vulnerability'

// V5 — Vendor advisories (see 2026-04-20-vulns-v5-vendor-advisories-plan.md).
export type VendorName = 'msrc' | 'redhat' | 'ubuntu' | 'cisco' | 'paloalto' | 'suse'
export type NormalisedSeverity = 'low' | 'medium' | 'high' | 'critical'
export type EpssStatus = 'scored' | 'pending' | 'not_scored' | 'unknown'

export interface VendorAdvisory {
  id: string
  cveId: string
  vendor: VendorName
  advisoryId: string
  url: string
  fixVersion: string | null
  publishedAt: string | null
  rawSeverity: string | null
  normalisedSeverity: NormalisedSeverity | null
  lastSyncedAt: string
  suppressedAt: string | null
  suppressedBy: string | null
}

/**
 * KAN-864 — the takeaway, falling back to `summary` when `takeaway` is null (a
 * legacy row) or blank (codex #1540 H1), as `articleTakeaway` in
 * @intelhub/shared does. A missing field throws, since falling back would
 * silently read the source.
 */
export function articleTakeaway(article: Pick<Article, 'summary' | 'takeaway'>): string {
  if (article.takeaway === undefined) {
    throw new TypeError('articleTakeaway: article has no `takeaway` field')
  }
  return article.takeaway === null || !/\S/.test(article.takeaway) ? article.summary : article.takeaway
}

export interface Article {
  id: string
  type: ArticleType
  title: string
  sourceUrl: string
  publisher: string
  publisherUrl?: string | null
  publishedAt: string
  /** The source summary (on a legacy row it may hold the takeaway: see `takeaway`). */
  summary: string
  /**
   * KAN-864 — the summarise step's CISO takeaway. Null on legacy rows, whose
   * takeaway was written over `summary`: read it via `articleTakeaway`.
   */
  takeaway: string | null
  recommendedActions?: string[] | null
  category?: Category | null
  section?: Section | null
  confidence?: Confidence | null
  riskScore: number
  severity?: Severity | null
  jurisdiction?: string[] | null
  industries?: string[] | null
  affectedVendors?: string[] | null
  relatedThreatActors?: string[] | null
  relatedCves?: string[] | null
  discourseUrl?: string | null
  isFeatured?: boolean | null
  status: ContentStatus
  latitude?: number | null
  longitude?: number | null
  aiProcessedAt?: string | null
  fullText?: string | null
  fullTextFetchedAt?: string | null
  fullTextMeta?: import('@intelhub/shared').ExploitDbExtraction | null
  createdAt: string
  updatedAt: string
}

export interface RegulatoryItem {
  id: string
  type: RegulatoryType
  title: string
  jurisdiction: string
  deadline?: string | null
  description: string
  sourceUrl: string
  publishedAt: string
  status: ContentStatus
  createdAt: string
  updatedAt: string
}

export interface Vulnerability {
  id: string
  cveId: string
  title: string
  description?: string | null
  cvssScore?: string | null
  cvssVector?: string | null
  epssScore?: string | null
  epssStatus?: EpssStatus | null
  severity?: Severity | null
  exploitationStatus?: ExploitationStatus | null
  affectedProducts?: unknown
  /**
   * Phase 2 vendor unification (Plan T9 + T11) — detail endpoint emits
   * `VendorRef[]` directly on this field, replacing the legacy
   * `affectedVendorsResolved` projection AND the raw `uuid[]` shape.
   *
   * Detail endpoint (`GET /api/vulnerabilities/:id`): present, full list
   * in INPUT order.
   * List endpoint (`GET /api/vulnerabilities`): the raw `affectedVendors`
   * field is STRIPPED — list rows expose `topAffectedVendors` (capped at
   * 2, alpha-sorted) plus `affectedVendorCount`. Optional in the type so
   * list rows typecheck.
   */
  affectedVendors?: VendorRef[] | null
  /**
   * Phase 2 vendor unification (Plan T9 + T11) — detail endpoint emits
   * `PersonRef[]` from `vulnerabilities.affected_persons uuid[]`. Empty
   * for the vast majority of CVEs until the pipeline classifier (Phase 2
   * pipeline routing) populates the column.
   */
  affectedPersons?: PersonRef[] | null
  // Phase 2 T9 list endpoint: top 1-2 vendors + total count. Each entry
  // is a `VendorRef` (carries `tier` so the list can render the Tier-1 pill).
  topAffectedVendors?: VendorRef[] | null
  affectedVendorCount?: number | null
  patchAvailable?: boolean | null
  patchUrl?: string | null
  references?: unknown
  firstSeen?: string | null
  lastUpdated?: string | null
  // V1 admin override provenance — populated by Slice 2 public response projection.
  adminNotes?: string | null
  exploitationStatusChangedAt?: string | null
  exploitationStatusChangedBy?: string | null
  patchAvailableChangedAt?: string | null
  patchAvailableChangedBy?: string | null
  // V2 — CISA-KEV lifecycle tracking (see 2026-04-20-vulns-v2-kev-deescalation-design.md).
  kevFirstSeenAt?: string | null
  kevLastSeenAt?: string | null
  kevRemovedAt?: string | null
  previouslyKevFlagged?: boolean | null
  // V3 — Patch classifier projection (see 2026-04-20-vulns-v3-patch-adapter-plan.md).
  // Populated by the public `GET /api/vulnerabilities/:id` response.
  fixVersionHint?: string | null
  primaryAdvisoryUrl?: string | null
  patchClassifiedAt?: string | null
  // V4 — FIRST.org EPSS refresh (see 2026-04-20-vulns-v4-epss-refresh-design.md).
  // Percentile serialized as a string decimal in [0, 1] (Drizzle `decimal(5,4)`).
  epssPercentile?: string | null
  epssRefreshedAt?: string | null
  // V5 — Vendor advisories projection. Populated by the public
  // `GET /api/vulnerabilities/:id` response (non-suppressed rows only,
  // sorted by `publishedAt` DESC).
  vendorAdvisories?: VendorAdvisory[]
  // V6 — CIRCL enrichment (see 2026-04-20-vulns-v6-circl-enrichment-plan.md).
  // `circlEnrichedAt` is a timestamp on the vulnerabilities table; the
  // `enrichment` sub-object is projected by the public `GET /:id` handler
  // from four child tables + an `attack_techniques` lookup.
  circlEnrichedAt?: string | null
  enrichment?: VulnEnrichment
  /**
   * Ontology tags aggregated from every article that mentions this CVE
   * (`articles.related_cves` overlap). Surfaced on the public
   * `GET /api/vulnerabilities/:id` projection so `<ChannelCTA>` can resolve
   * a thematic chat-channel CTA without an extra API hop. Always present
   * (empty arrays if no related articles carry the relevant taxonomy);
   * optional in the type to stay backwards-compatible with cached responses
   * served by an older API.
   */
  ontologyTags?: {
    'security-domain': string[]
    industry: string[]
    region: string[]
  }
  createdAt: string
  updatedAt: string
}

// ─── V6 CIRCL enrichment types ────────────────────────────────────────────────

export interface VulnCpe {
  id: string
  cveId: string
  cpe: string
  vendor: string
  product: string
  version: string | null
}

export interface VulnCwe {
  cveId: string
  cweId: string
}

export interface VulnExploit {
  id: string
  cveId: string
  url: string
  source: string | null
  publishedAt: string | null
}

export interface AttackTechnique {
  externalId: string
  name: string
  description: string | null
  tactic: string | null
}

export interface VulnEnrichment {
  circlEnrichedAt: string | null
  cpes: VulnCpe[]
  cwes: VulnCwe[]
  exploits: VulnExploit[]
  techniques: AttackTechnique[]
}

export interface ThreatActor {
  id: string
  name: string
  aliases?: string[] | null
  description?: string | null
  attribution?: string | null
  originCountry?: string | null
  knownTtps?: unknown
  targetSectors?: string[] | null
  targetRegions?: string[] | null
  active?: boolean | null
  lastSeen?: string | null
  references?: unknown
  mitreId?: string | null
  mispUuid?: string | null
  malpediaId?: string | null
  sourcesLastSyncedAt?: string | null
  createdAt: string
  updatedAt: string
  /** KAN-736 R20. `campaign` arrives only when a fetch followed the R22 redirect. */
  recordType?: 'actor' | 'campaign' | null
  typeStatus?: ThreatActorTypeStatus
}

export type ThreatActorTypeStatus = 'confirmed' | 'unverified' | 'contested'

/** KAN-736 R23/R28 — one assertion; analyst fields only on an analyst entry. */
export interface AttributionAssertion {
  originSource: string
  evidenceType: 'direct_structured' | 'analyst_override'
  firstSeen: string | null
  lastSeen: string | null
  intervalBasis: string
  confidence: 'high' | 'medium' | 'low' | null
  sourceConfidenceRaw: string | null
  citations: string[]
  assertedBy?: string
  assertedAt?: string
  basis?: string
}

export interface UnrenderedAttributions {
  total: number
  byToken: Record<string, number>
  untrackedMitreGroupIds: string[]
}

export interface TypeAuthority {
  authority: string
  assertedType: 'actor' | 'campaign'
  assertedAt: string | null
}

/** Response shape of `GET /api/threat-actors/:id` — see api projectThreatActorDetail(). */
export interface ThreatActorDetail extends ThreatActor {
  originCountryIso2: string | null
  confidenceBadge: 'high' | 'medium' | 'low' | null
  activeSince: string | null
  recentIncidentIds: string[]
  /** KAN-736 R23 (KAN-830 PR 9/10). Absent from older API builds. */
  attributedCampaigns?: Array<{
    campaignId: string
    name: string
    recordType: 'actor' | 'campaign' | null
    typeStatus: ThreatActorTypeStatus
    assertions: AttributionAssertion[]
  }>
  unrenderedAttributions?: UnrenderedAttributions
  typeAuthorities?: TypeAuthority[]
}

/** `GET /api/campaigns` row (KAN-736 R23). */
export interface CampaignSummary {
  id: string
  name: string
  aliases: string[]
  recordType: 'campaign'
  firstSeen: string | null
  lastSeen: string | null
  renderedAttributionCount: number
}

/** `GET /api/campaigns/:id` (KAN-736 R23). `recordType` differs only after an R22 redirect. */
export interface CampaignDetail {
  id: string
  name: string
  aliases: string[]
  description: string | null
  recordType: 'actor' | 'campaign' | null
  typeStatus: ThreatActorTypeStatus
  firstSeen: string | null
  lastSeen: string | null
  attributedTo: Array<{
    actorId: string
    name: string
    recordType: 'actor' | 'campaign' | null
    typeStatus: ThreatActorTypeStatus
    parent: null
    assertions: AttributionAssertion[]
  }>
  sourceTally: { feeds: Record<string, number>; analystAssessments: number }
  unrenderedAttributions: UnrenderedAttributions
  orderIsDisplayOnly: true
  typeAuthorities: TypeAuthority[]
}

export interface Note {
  id: string
  contentType: ContentType
  contentId: string
  authorId: string
  text: string
  isFlagged?: boolean | null
  createdAt: string
}

export interface Vendor {
  id: string
  name: string
  /** KAN-32: on the wire since Phase 1 vendor unification (vendors.slug is
   *  NOT NULL and routes/vendors.ts selects all columns); previously untyped. */
  slug: string
  aliases?: string[] | null
  category?: string | null
  website?: string | null
  logoUrl?: string | null
  createdAt: string
}

/**
 * Minimal supply-chain provider summary returned by `GET /api/providers`.
 * Wire is `{ providers: ProviderSummary[] }`. Only the fields the settings
 * muted-providers chip input needs are typed here; the full shape lives in
 * `apps/api/src/supply-chain/projectors/projectProviderSummary.ts`.
 */
export interface ProviderSummary {
  id: string
  slug: string
  name: string
  category: 'cloud' | 'identity'
  active: boolean
}

/**
 * KAN-822 T3/T9 — the reader's saved Vendor updates preference, as the
 * preferences envelope reports it (`vendorContent`). Off unless `enabled`.
 */
export interface VendorContentPreference {
  enabled: boolean
  subjectVendorIds: string[]
  subtypes: string[]
}

export interface UserPreferences {
  id: string
  userId: string
  /** KAN-822 T9 — read from the envelope; never sent back through the generic PUT. */
  vendorContent?: VendorContentPreference
  defaultRegions?: string[] | null
  defaultIndustries?: string[] | null
  defaultContentTypes?: string[] | null
  severityThreshold?: Severity | null
  emailEnabled?: boolean | null
  emailSeverityThreshold?: Severity | null
  criticalAlertsEnabled?: boolean | null
  // Plan B — Ransomware leak-site v1 prefs (Plan 2 T20)
  briefingIncludesRansomware?: boolean | null
  ransomwareBurstAlertsEnabled?: boolean | null
  ransomwareBurstAlertThreshold?: number | null
  ransomwareRealtimeToastsEnabled?: boolean | null
  // Plan C — Supply chain & cloud security v1 prefs (Plan 2 T22)
  briefingIncludesSupplyChain?: boolean | null
  supplyChainCriticalAlertsEnabled?: boolean | null
  mutedProviders?: string[] | null
  // KAN-32 (R12 / O3) — session-replay objection. Also carried on
  // `GET /api/auth/me` so replay can never start before it is known.
  replayOptOut?: boolean | null
  // KAN-32 F1 — ISO timestamp of the analytics-notice acknowledgement, or
  // null/absent when the user has not been notified yet. READ-ONLY on this
  // type: the client acknowledges by sending `analyticsNoticeAcknowledged`
  // (see `useAcknowledgeAnalyticsNotice`), and the server stamps the time.
  analyticsNoticeSeenAt?: string | null
  // KAN-519 — account-level analytics objection / future-capture suppression.
  analyticsSuppressedAt?: string | null
  // KAN-810 / KAN-836 — ISO time the user turned the weekly regulatory digest
  // off (one-click unsubscribe or Settings); null means subscribed. READ-ONLY
  // here: change it with `useSetRegulatoryDigestSubscription`.
  regulatoryDigestOptedOutAt?: string | null
  updatedAt: string
  trackedVendors?: string[]
  keywordWatches?: KeywordWatch[]
}

export interface KeywordWatch {
  id: string
  keyword: string
  boostWeight?: number | null
}

export interface SavedSearch {
  id: string
  userId: string
  name: string
  filters: Record<string, unknown>
  createdAt: string
}

export interface Assignment {
  id: string
  contentType: ContentType
  contentId: string
  assigneeId: string
  status: string
  createdAt: string
}

// ─── Sources ─────────────────────────────────────────────────────────────────

export type SourceType = 'rss' | 'api' | 'scraper' | 'manual'
export type SourceSchedule = 'hourly' | '6h' | 'daily'

/**
 * KAN-854 — derived, read-only label computed by the API (never stored). A
 * `dedicated_job` row is a marker whose data is written by a dedicated pipeline
 * job; `registryScheduleDrivesJob` is false when "Fetch now" has nothing to do.
 */
export type SourceManagement =
  | { kind: 'registry' }
  | {
      kind: 'dedicated_job'
      job: 'nvd' | 'cisa-kev' | 'epss' | 'github-advisory'
      label: string
      detail: string
      registryScheduleDrivesJob: boolean
    }

export interface Source {
  id: string
  name: string
  type: SourceType
  url: string | null
  config: Record<string, unknown> | null
  schedule: string | null
  enabled: boolean
  lastFetchedAt: string | null
  lastError: string | null
  errorCount: number
  tier: SourceTier
  parentOrg: string | null
  tierNotes: string | null
  createdAt: string
  updatedAt: string
  /** Absent from an API that predates KAN-854; treat as `registry`. */
  management?: SourceManagement
}

// ─── Regulatory Actions ──────────────────────────────────────────────────────

export type RegulatoryActionType = 'breach_disclosure' | 'enforcement_action'

export interface RegulatoryAction {
  id: string
  type: RegulatoryActionType
  externalId: string
  organisationName: string
  jurisdiction: string | null
  decisionDate: string | null
  severity: Severity | null
  sourceUrl: string | null
  summary: string | null
  details: Record<string, unknown> | null
  articleId: string | null
  sourceId: string | null
  createdAt: string
  updatedAt: string
}

export interface RegulatoryActionsStats {
  byJurisdiction: { jurisdiction: string | null; count: number }[]
  byType: { type: string; count: number }[]
  bySeverity: { severity: string | null; count: number }[]
  total: number
  totalFines: number
}

export interface RegulatoryActionFilters {
  [key: string]: string | number | boolean | string[] | undefined | null
  type?: string
  jurisdiction?: string
  severity?: string
  /** Framework slug (Stage 2 of /regulatory wave-1). */
  framework?: string
  /** A1g forward fix: organisation-name substring search (server-side). */
  search?: string
  page?: number
  limit?: number
}

// ─── Filter types ─────────────────────────────────────────────────────────────

export interface ArticleFilters {
  [key: string]: string | number | boolean | string[] | undefined | null
  type?: string
  region?: string
  industry?: string
  severity?: string
  search?: string
  limit?: number
  offset?: number
}

export interface VulnerabilityFilters {
  [key: string]: string | number | boolean | string[] | undefined | null
  severity?: string
  exploitation_status?: string
  patch_status?: string
  search?: string
  // Phase B T6/T11: server-side filter on `affected_vendors` (uuid).
  // Backed by the GIN index `idx_vulns_affected_vendors_gin` (migration 0054).
  vendor?: string
}

export const THREAT_ACTOR_SORT_OPTIONS = [
  'alpha',
  'linked-stories',
] as const

export type ThreatActorSort = (typeof THREAT_ACTOR_SORT_OPTIONS)[number]

export const DEFAULT_THREAT_ACTOR_SORT: ThreatActorSort = 'alpha'

export interface ThreatActorFilters {
  [key: string]: string | number | boolean | string[] | undefined | null
  attribution?: string
  sort?: ThreatActorSort
}

/**
 * Aggregate response from `GET /api/threat-actors/kpis`.
 *
 * Computed corpus-wide (respecting the SAME filters the list endpoint accepts)
 * so the rail tiles stay coherent with the list when a filter narrows the
 * view. Replaces the previous client-side roll-up which saturated at
 * DEFAULT_LIMIT (50). Server contract: see
 * `apps/api/src/routes/threat-actors.ts` `GET /kpis`.
 */
export interface ThreatActorKpis {
  activeCount: number | null
  countriesCount: number | null
  attributionCounts: {
    state: number
    criminal: number
    hacktivist: number
    unknown: number
  }
  dominantAttribution:
    | 'state'
    | 'criminal'
    | 'hacktivist'
    | 'unknown'
    | null
}

// ─── Dashboard filter state ───────────────────────────────────────────────────

export interface DashboardFilters {
  search: string
  types: ArticleType[]
  regions: string[]
  industries: string[]
  severities: Severity[]
  timeWindow: '24h' | '7d' | '30d' | '90d'
  sort: 'date' | 'risk' | 'relevance'
}

export const DEFAULT_FILTERS: DashboardFilters = {
  search: '',
  types: [],
  regions: [],
  industries: [],
  severities: [],
  timeWindow: '7d',
  sort: 'date',
}
